import { useCallback, useEffect, useRef } from 'react';
import {
  parseMarkupToTokens,
  serializeDomToMarkup,
} from 'src/modules/shared/utils/editor/inline-tokens-editor.util';
import { insertSlashCommand } from 'src/modules/shared/utils/editor/slash-insert.util';
import type { SlashCommand } from 'src/modules/shared/types/editor/slash-commands.types';
import type { InlineTokensEditorProps } from 'src/modules/shared/types/editor/inline-tokens.types';
import { type InlineTokensSlashState } from 'src/modules/shared/components/molecules/editor/inline-tokens-slash-menu';
import { applyEditorTextFeatures } from 'src/modules/shared/utils/editor/editor-text.util';
import type {
  EditorKey,
  EditorKeyEventFlags,
  EditorTextFeaturesConfig,
  EditorTextState,
} from 'src/modules/shared/types/editor/editor.type';
import { useSlashCommands } from 'src/modules/shared/hooks/editor/slash-commands';

/** Default styling for token chips in the editor */
const defaultChipClasses =
  'inline align-baseline rounded-md px-1 py-0.5 font-normal whitespace-pre-wrap cursor-default select-none bg-gray-100 text-gray-700';

/**
 * Calculates the text length of a node, accounting for token markup.
 * For text nodes, returns character length.
 * For token elements (with data-token-kind and data-token-id), returns the length of the serialized markup.
 * For BR elements, returns 1.
 * For other elements, recursively sums child node lengths.
 */
const getNodeTextLength = (node: Node): number => {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node as Text).data.length;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    const tokenKind = el.dataset.tokenKind;
    const tokenId = el.dataset.tokenId;

    if (tokenKind && tokenId) {
      return `[[${tokenKind}:${tokenId}]]`.length;
    }

    if (el.tagName === 'BR') {
      return 1;
    }

    return Array.from(el.childNodes).reduce(
      (len, child) => len + getNodeTextLength(child),
      0
    );
  }

  return 0;
};

/**
 * Converts a DOM node position to a text index within the editor's content.
 * Walks the DOM tree from root, accumulating text length until reaching the target node,
 * then returns the index adjusted by the target offset.
 */
const getIndexForPosition = (
  root: HTMLElement,
  targetNode: Node,
  targetOffset: number
): number | null => {
  let index = 0;
  let result: number | null = null;

  const walk = (node: Node): boolean => {
    if (node === targetNode) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node as Text).data;
        const clampedOffset = Math.max(0, Math.min(targetOffset, text.length));
        result = index + clampedOffset;
        return true;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tokenKind = el.dataset.tokenKind;
        const tokenId = el.dataset.tokenId;

        if (tokenKind && tokenId) {
          const tokenLength = `[[${tokenKind}:${tokenId}]]`.length;
          result = index + (targetOffset <= 0 ? 0 : tokenLength);
          return true;
        }

        if (el.tagName === 'BR') {
          result = index + (targetOffset <= 0 ? 0 : 1);
          return true;
        }

        const children = Array.from(el.childNodes);
        const clampedOffset = Math.max(
          0,
          Math.min(targetOffset, children.length)
        );
        for (let i = 0; i < clampedOffset; i += 1) {
          index += getNodeTextLength(children[i] as Node);
        }
        result = index;
        return true;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      index += (node as Text).data.length;
      return false;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tokenKind = el.dataset.tokenKind;
      const tokenId = el.dataset.tokenId;

      if (tokenKind && tokenId) {
        index += `[[${tokenKind}:${tokenId}]]`.length;
        return false;
      }

      if (el.tagName === 'BR') {
        index += 1;
        return false;
      }

      const children = Array.from(el.childNodes);
      for (const child of children) {
        if (walk(child)) return true;
      }
    }

    return false;
  };

  walk(root);

  return result;
};

/**
 * Extracts the current selection range from the DOM.
 * Returns start and end indices relative to the editor's text content.
 */
const getSelectionRangeFromDom = (
  root: HTMLElement
): { start: number; end: number } | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;

  if (!anchorNode || !focusNode) {
    return null;
  }

  const anchorOffset = selection.anchorOffset ?? 0;
  const focusOffset = selection.focusOffset ?? 0;

  const anchorIndex = getIndexForPosition(root, anchorNode, anchorOffset);
  const focusIndex = getIndexForPosition(root, focusNode, focusOffset);

  if (anchorIndex == null || focusIndex == null) {
    return null;
  }

  const start = Math.min(anchorIndex, focusIndex);
  const end = Math.max(anchorIndex, focusIndex);

  return { start, end };
};

/**
 * Gets the current caret position as a text index.
 * Returns the end of the selection range.
 */
const getCaretIndexFromDom = (root: HTMLElement): number | null => {
  const range = getSelectionRangeFromDom(root);
  if (!range) return null;
  return range.end;
};

/**
 * Sets the caret position at a specific text index within the editor.
 * Walks the DOM to find the corresponding node and offset, then updates the selection.
 */
const setCaretFromIndex = (root: HTMLElement, targetIndex: number): void => {
  const selection = window.getSelection();
  if (!selection) return;

  let remaining = targetIndex;
  let targetNode: Node | null = null;
  let targetOffset = 0;

  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).data;
      if (remaining <= text.length) {
        targetNode = node;
        targetOffset = remaining;
        return true;
      }
      remaining -= text.length;
      return false;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tokenKind = el.dataset.tokenKind;
      const tokenId = el.dataset.tokenId;

      if (tokenKind && tokenId) {
        const tokenLength = `[[${tokenKind}:${tokenId}]]`.length;
        const parent = el.parentNode ?? root;
        const siblings = Array.from(parent.childNodes);
        const indexInParent = siblings.indexOf(el);
        const before = remaining === 0;

        if (remaining <= tokenLength) {
          targetNode = parent;
          targetOffset = before ? indexInParent : indexInParent + 1;
          return true;
        }

        remaining -= tokenLength;
        return false;
      }

      if (el.tagName === 'BR') {
        const parent = el.parentNode ?? root;
        const siblings = Array.from(parent.childNodes);
        const indexInParent = siblings.indexOf(el);
        const before = remaining === 0;

        if (remaining <= 1) {
          targetNode = parent;
          targetOffset = before ? indexInParent : indexInParent + 1;
          return true;
        }

        remaining -= 1;
        return false;
      }

      const children = Array.from(el.childNodes);
      for (const child of children) {
        if (walk(child)) return true;
      }
    }

    return false;
  };

  walk(root);

  if (!targetNode) {
    targetNode = root;
    targetOffset = root.childNodes.length;
  }

  const range = document.createRange();
  range.setStart(targetNode, targetOffset);
  range.collapse(true);

  selection.removeAllRanges();
  selection.addRange(range);
};

type UseInlineTokensEditorArgs = Pick<
  InlineTokensEditorProps,
  | 'value'
  | 'onChange'
  | 'disabled'
  | 'definitions'
  | 'slashCommands'
  | 'getChipClassName'
  | 'editorFeatures'
>;

/**
 * Hook for managing inline token editor state and interactions.
 * Handles rendering tokens, managing slash commands, processing keyboard input,
 * and coordinating caret position across DOM updates.
 */
export const useInlineTokensEditor = ({
  value,
  onChange,
  disabled = false,
  definitions,
  slashCommands,
  getChipClassName,
  editorFeatures,
}: UseInlineTokensEditorArgs) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastAppliedValueRef = useRef<string | null>(null);
  const pendingCaretIndexRef = useRef<number | null>(null);
  const {
    state: genericSlashState,
    openForSection,
    handleKeyDown: handleSlashKeyDown,
    close: closeSlashMenu,
    getFilteredCommands,
  } = useSlashCommands<'inline'>({
    commands: slashCommands ?? [],
  });

  const resolveChipClassName = useCallback(
    (kind: string): string => getChipClassName?.(kind) ?? defaultChipClasses,
    [getChipClassName]
  );

  useEffect(() => {
    if (!editorRef.current) return;
    if (value === lastAppliedValueRef.current) return;

    lastAppliedValueRef.current = value;

    const tokens = parseMarkupToTokens(value, definitions);
    const root = editorRef.current;
    root.innerHTML = '';

    const appendText = (text: string) => {
      const segments = text.split(/\n/);
      segments.forEach((segment, index) => {
        if (index > 0) {
          root.appendChild(document.createElement('br'));
        }
        if (segment) {
          root.appendChild(document.createTextNode(segment));
        }
      });
    };

    tokens.forEach((token) => {
      if ('value' in token) {
        appendText(token.value);
        return;
      }

      const span = document.createElement('span');
      span.contentEditable = 'false';
      span.className = resolveChipClassName(token.kind);
      span.dataset.tokenKind = token.kind;
      span.dataset.tokenId = token.id;
      span.textContent = token.label;

      root.appendChild(span);
    });

    if (pendingCaretIndexRef.current != null) {
      setCaretFromIndex(root, pendingCaretIndexRef.current);
      pendingCaretIndexRef.current = null;
    }
  }, [value, definitions, resolveChipClassName]);

  const handleInput = () => {
    if (!editorRef.current || disabled) return;
    const raw = serializeDomToMarkup(editorRef.current);
    const next = raw.trim().length === 0 ? '' : raw;

    if (next === '') {
      editorRef.current.innerHTML = '';
    }

    lastAppliedValueRef.current = next;
    onChange(next);
  };

  const commitSlashCommand = (command: SlashCommand) => {
    if (!editorRef.current || !genericSlashState) {
      return;
    }

    const root = editorRef.current;
    const current = serializeDomToMarkup(root);

    const { nextText, caret } = insertSlashCommand({
      text: current,
      startOffset: genericSlashState.startOffset,
      query: genericSlashState.query,
      command,
    });

    closeSlashMenu();
    pendingCaretIndexRef.current = caret;
    onChange(nextText);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (disabled) return;

    const root = editorRef.current;

    if (root && genericSlashState?.isOpen) {
      const current = serializeDomToMarkup(root);
      const result = handleSlashKeyDown(
        'inline',
        event as React.KeyboardEvent,
        current
      );
      if (result.handled) {
        if (result.commitText != null) {
          pendingCaretIndexRef.current = result.caret ?? null;
          onChange(result.commitText);
        }
        return;
      }
    }

    if (
      (event.key === 'Tab' || event.key === 'Enter') &&
      !genericSlashState?.isOpen &&
      root &&
      editorFeatures
    ) {
      const selectionRange = getSelectionRangeFromDom(root);
      const current = serializeDomToMarkup(root);

      const selection = selectionRange ?? {
        start: current.length,
        end: current.length,
      };

      const state: EditorTextState = {
        text: current,
        selection,
      };

      const flags: EditorKeyEventFlags = {
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
      };

      const result = applyEditorTextFeatures(
        state,
        event.key as EditorKey,
        flags,
        editorFeatures as EditorTextFeaturesConfig
      );

      if (result) {
        event.preventDefault();
        pendingCaretIndexRef.current = result.selection.end;
        onChange(result.text);
        return;
      }
    }

    if (
      event.key === '/' &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      if (root) {
        const caretIndex = getCaretIndexFromDom(root);
        if (caretIndex != null) {
          openForSection('inline', caretIndex);
        }
      }
      return;
    }
  };

  const commands = slashCommands ?? [];
  const filteredCommands = genericSlashState ? getFilteredCommands() : commands;

  const hasContent = value.trim().length > 0;

  return {
    editorRef,
    hasContent,
    slashState: genericSlashState
      ? ({
          isOpen: genericSlashState.isOpen,
          query: genericSlashState.query,
          activeIndex: genericSlashState.activeIndex,
        } as InlineTokensSlashState)
      : null,
    filteredCommands,
    handleInput,
    handleKeyDown,
    commitSlashCommand,
  };
};
